import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Shows a native <dialog> as a modal while `open`. Closing sets `data-closing` and waits for the
 * dialog's exit animation (CSS on `[data-closing]`) before `close()`, so render the content while
 * the returned flag is true rather than while `open` is. Escape goes through `onClose` too, so it
 * animates like the backdrop and the × do.
 */
export function useModalDialog(ref: RefObject<HTMLDialogElement | null>, open: boolean, onClose: () => void): boolean {
  const [shown, setShown] = useState(open);
  if (open && !shown) setShown(true);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const onCancel = (e: Event) => {
      e.preventDefault();
      onCloseRef.current();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => dialog.removeEventListener('cancel', onCancel);
  }, [ref]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    const leave = (leaving: boolean) => {
      dialog.inert = leaving;
      if (leaving) {
        dialog.dataset.closing = '';
        dialog.setAttribute('aria-hidden', 'true');
      } else {
        delete dialog.dataset.closing;
        dialog.removeAttribute('aria-hidden');
      }
    };
    if (open) {
      leave(false);
      if (!dialog.open) dialog.showModal();
      return;
    }
    if (!dialog.open) {
      setShown(false);
      return;
    }
    // A dialog on its way out is already gone for input and assistive tech.
    leave(true);
    let current = true;
    const leaving = dialog.getAnimations?.() ?? [];
    void Promise.allSettled(leaving.map((a) => a.finished)).then(() => {
      if (!current) return;
      leave(false);
      dialog.close();
      setShown(false);
    });
    return () => {
      current = false;
    };
  }, [ref, open]);

  return open || shown;
}
